// import type only. @parcel/tools ships the ~1000-line native tool registry; verbatimModuleSyntax
// erases a type-only import at build, so pulling these two aliases from it costs zero bundle
// bytes. A VALUE import from @parcel/tools in this file would ship the whole registry into the
// browser bundle for a settings page that only ever needs the wire rows the API already sent --
// do not add one.
import type { NativeToolEffect, SpecPolicyReason } from '@parcel/tools';
import type { SpecLaunchState, SpecToolDecision } from '@parcel/types';
import { ApiError, apiFetch } from './api.js';

/**
 * Typed client for Settings > Spec AI > Tools: reads the effective policy for
 * every native tool in a workspace and writes the two layers a member can
 * change (the workspace default, and their own personal override), one tool
 * at a time or as a category-wide default.
 *
 * Wire shapes are `policyRowSchema` / `listResponseSchema` /
 * `batchResponseSchema` in apps/api/src/routes/spec-tools.ts. That file is
 * the authority for the `SpecToolWireRow*` types below -- re-read it before
 * changing this mapping, rather than trusting this file's own history.
 */

// ---------------------------------------------------------------------------
// Client types
// ---------------------------------------------------------------------------

export interface SpecToolSettingsRow {
  name: string;
  /** Derived from `name` by {@link deriveSpecToolTitle}. The API sends no label. */
  title: string;
  description: string;
  category: string;
  effect: NativeToolEffect;
  registryFloor: SpecToolDecision;
  workspaceDecision: SpecToolDecision | null;
  memberDecision: SpecToolDecision | null;
  memberCanAlwaysAllow?: boolean;
  operationSpecificPolicy?: boolean;
  /** What actually governs a call right now: floor, workspace, and member combined. */
  effectiveDecision: SpecToolDecision;
  reason: SpecPolicyReason;
  revision: number;
  lastChangedBy: { memberId: string; displayName: string | null } | null;
  lastChangedAt: string | null;
  /**
   * Uses of this tool in the audit window (see `auditWindowDays`). `null`
   * means "not available to you" -- not requested, or you are not
   * owner/admin -- which is a different fact from `0` ("never used"). Never
   * coalesce this to 0 anywhere downstream of this client.
   */
  recentAuditCount: number | null;
}

export interface SpecToolsSettings {
  workspaceId: string;
  memberId: string;
  role: 'owner' | 'admin' | 'member';
  plan: string;
  canManage: boolean;
  launchState: SpecLaunchState;
  auditWindowDays: number;
  tools: SpecToolSettingsRow[];
}

// ---------------------------------------------------------------------------
// Wire shapes (apps/api/src/routes/spec-tools.ts, policyRowSchema and friends)
//
// Trimmed to the fields this client reads. `version`, `defaultPolicy`,
// `requiredScope`, `requiredRole`, `requiredPlan`, and `idempotency` are real
// wire fields nobody on this page uses, so they are left off these types
// rather than carried through unread.
// ---------------------------------------------------------------------------

interface SpecToolWireRow {
  name: string;
  category: string;
  effect: NativeToolEffect;
  description: string;
  registryFloor: SpecToolDecision;
  decision: SpecToolDecision;
  reason: SpecPolicyReason;
  workspaceDecision: SpecToolDecision | null;
  memberDecision: SpecToolDecision | null;
  memberCanAlwaysAllow?: boolean;
  operationSpecificPolicy?: boolean;
  revision: number;
  updatedAt: string | null;
  updatedBy: string | null;
  updatedByName: string | null;
  recentAuditCount: number | null;
}

interface SpecToolsListWireResponse {
  workspaceId: string;
  memberId: string;
  role: 'owner' | 'admin' | 'member';
  plan: string;
  canManage: boolean;
  launchState: SpecLaunchState;
  auditWindowDays: number;
  tools: SpecToolWireRow[];
}

type SpecToolBatchWireResult =
  | { index: number; status: 'ok'; record: SpecToolWireRow }
  | { index: number; status: 'error'; error: { code: string; message: string } };

interface SpecToolBatchWireResponse {
  results: SpecToolBatchWireResult[];
}

/**
 * One row, wire to client. Four renames from the raw JSON, each because the
 * wire name is either ambiguous next to a sibling field or names something
 * this client models differently:
 *
 *  - `decision` -> `effectiveDecision` (ambiguous next to workspace/member).
 *  - `updatedBy` + `updatedByName` -> one `lastChangedBy` object, null exactly
 *    when `updatedBy` is null. An id with an unresolved name surfaces as
 *    `{ memberId, displayName: null }`, never a placeholder string.
 *  - `updatedAt` -> `lastChangedAt`.
 *  - `recentAuditCount` passes through untouched, INCLUDING null. Coalescing
 *    it here would be indistinguishable from a workspace that has never used
 *    the tool.
 */
function toSpecToolSettingsRow(wire: SpecToolWireRow): SpecToolSettingsRow {
  return {
    name: wire.name,
    title: deriveSpecToolTitle(wire.name),
    description: wire.description,
    category: wire.category,
    effect: wire.effect,
    registryFloor: wire.registryFloor,
    workspaceDecision: wire.workspaceDecision,
    memberDecision: wire.memberDecision,
    memberCanAlwaysAllow: wire.memberCanAlwaysAllow,
    operationSpecificPolicy: wire.operationSpecificPolicy,
    effectiveDecision: wire.decision,
    reason: wire.reason,
    revision: wire.revision,
    lastChangedBy: wire.updatedBy
      ? { memberId: wire.updatedBy, displayName: wire.updatedByName }
      : null,
    lastChangedAt: wire.updatedAt,
    recentAuditCount: wire.recentAuditCount,
  };
}

/**
 * `search_accounts` -> "Search accounts"; `workspace_upsert_account` ->
 * "Workspace upsert account". The API sends no display label: deriving one
 * mechanically here keeps the registry the single source of tool names,
 * with no 36-entry title table anywhere to fall out of sync with it.
 */
export function deriveSpecToolTitle(name: string): string {
  return name
    .split('_')
    .map((word, index) => (index === 0 ? capitalizeFirstWord(word) : word))
    .join(' ');
}

/** A step row's title: manage_skill names its command ("Create skill"), every other tool its name. */
export function deriveSpecToolStepTitle(name: string, args?: unknown): string {
  const command =
    name === 'manage_skill' && typeof args === 'object' && args !== null
      ? (args as Record<string, unknown>).command
      : undefined;
  // Spec offers manage_skill's create and update only; anything else keeps the tool's own name.
  return command === 'create' || command === 'update'
    ? `${capitalizeFirstWord(command)} skill`
    : deriveSpecToolTitle(name);
}

function capitalizeFirstWord(word: string): string {
  return word.length === 0 ? word : `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
}

/**
 * The human half of a registry description: its first sentence.
 *
 * `descriptor.description` is written FOR THE MODEL, and it shows. The full
 * text of `search_accounts` is "Search Parcel accounts using filters, full-text
 * query, and cursor pagination. Supports relational filters (e.g. has_project,
 * without_project). Call search_fields first when unsure of valid fields or
 * enum values." Only the first sentence describes the tool; the rest is calling
 * technique, and `workspace_update_member` trails off into "Cannot demote the
 * last owner (409). Pro plan required. 0 credits."
 *
 * Every native description opens with a plain "verb the thing" sentence, so the
 * first one is reliably the summary a person wants. Derived rather than stored
 * for the same reason as {@link deriveSpecToolTitle}: a hand-written 36-entry
 * summary table is one more thing to fall out of sync with the registry.
 */
export function deriveSpecToolSummary(description: string): string {
  const trimmed = description.trim();
  // Split on sentence-ending punctuation followed by whitespace. The lookbehind
  // keeps the punctuation on the sentence it belongs to.
  const [first] = trimmed.split(/(?<=[.!?])\s+/);
  return first ?? trimmed;
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

const SPEC_TOOL_POLICY_ERROR_CODES = [
  'revision_conflict',
  'below_registry_floor',
  'below_workspace_policy',
  'unknown_tool',
  'forbidden',
] as const;

export type SpecToolPolicyErrorCode = (typeof SPEC_TOOL_POLICY_ERROR_CODES)[number];

const KNOWN_POLICY_ERROR_CODES = new Set<string>(SPEC_TOOL_POLICY_ERROR_CODES);

/**
 * A known tool-policy write rejection, thrown in place of a raw `ApiError` so
 * a page can branch on exactly what happened -- a revision conflict means
 * "reload and show the current value", a `below_workspace_policy` 400 means
 * "you can only tighten this" -- without string-matching a message or reading
 * `err.body` itself. An `ApiError` whose code this client does not recognize
 * is left as-is: an unhandled error is safer than a mislabeled one.
 */
export class SpecToolPolicyError extends Error {
  readonly code: SpecToolPolicyErrorCode;
  readonly status: number;

  constructor(status: number, code: SpecToolPolicyErrorCode, message: string) {
    super(message);
    this.name = 'SpecToolPolicyError';
    this.code = code;
    this.status = status;
  }
}

function toSpecToolPolicyError(err: unknown): unknown {
  if (!(err instanceof ApiError) || !err.body || typeof err.body !== 'object') return err;
  const body = err.body as { error?: { code?: unknown; message?: unknown } };
  const code = body.error?.code;
  if (typeof code !== 'string' || !KNOWN_POLICY_ERROR_CODES.has(code)) return err;
  const message = body.error?.message;
  return new SpecToolPolicyError(
    err.status,
    code as SpecToolPolicyErrorCode,
    typeof message === 'string' ? message : err.message,
  );
}

// ---------------------------------------------------------------------------
// Reads and single-tool writes
// ---------------------------------------------------------------------------

export async function listSpecTools(
  workspaceId: string,
  options: { includeAuditCounts?: boolean } = {},
): Promise<SpecToolsSettings> {
  // Never sends `surface`: that parameter selects the Developer MCP Worker's
  // authorization view (which deliberately skips display-name resolution),
  // and this client only ever serves the Spec settings page. Omitting it
  // takes the API's default, which is the surface we want.
  const query = options.includeAuditCounts ? '?include=audit_counts' : '';
  const wire = await apiFetch<SpecToolsListWireResponse>(
    `/workspaces/${workspaceId}/spec/tools${query}`,
  );
  return {
    workspaceId: wire.workspaceId,
    memberId: wire.memberId,
    role: wire.role,
    plan: wire.plan,
    canManage: wire.canManage,
    launchState: wire.launchState,
    auditWindowDays: wire.auditWindowDays,
    tools: wire.tools.map(toSpecToolSettingsRow),
  };
}

/** Owner/admin only. `expectedRevision` is the row's `revision` from the last list/write. */
export async function updateWorkspaceToolPolicy(
  workspaceId: string,
  toolName: string,
  decision: SpecToolDecision,
  expectedRevision: number,
): Promise<SpecToolSettingsRow> {
  try {
    const wire = await apiFetch<SpecToolWireRow>(
      `/workspaces/${workspaceId}/spec/tools/${encodeURIComponent(toolName)}`,
      { method: 'PATCH', body: JSON.stringify({ decision, expectedRevision }) },
    );
    return toSpecToolSettingsRow(wire);
  } catch (err) {
    throw toSpecToolPolicyError(err);
  }
}

/** Any member. `decision: null` clears the override and follows the workspace again. */
export async function updateMyToolPolicy(
  workspaceId: string,
  toolName: string,
  decision: SpecToolDecision | null,
): Promise<SpecToolSettingsRow> {
  try {
    const wire = await apiFetch<SpecToolWireRow>(
      `/workspaces/${workspaceId}/spec/tools/${encodeURIComponent(toolName)}/me`,
      { method: 'PATCH', body: JSON.stringify({ decision }) },
    );
    return toSpecToolSettingsRow(wire);
  } catch (err) {
    throw toSpecToolPolicyError(err);
  }
}

// ---------------------------------------------------------------------------
// Group default: preview (local) + apply (batch)
// ---------------------------------------------------------------------------

export interface SpecToolGroupPolicyException {
  toolName: string;
  /** Human-readable, names the floor that forbids the target decision. */
  reason: string;
}

export interface SpecToolGroupPolicyPreview {
  /** Rows this category's rows would actually change on apply. */
  applicable: readonly SpecToolSettingsRow[];
  /** Rows whose registry floor forbids the target decision entirely. */
  exceptions: readonly SpecToolGroupPolicyException[];
  /** Rows already at the target decision; applying would be a no-op write. */
  unchanged: readonly SpecToolSettingsRow[];
}

// Mirrors STRICTNESS in packages/tools/src/policy.ts. Duplicated rather than
// imported: that export is a VALUE from @parcel/tools, and this file may only
// take types from that package (see the header comment).
//
// Exported so the Tools settings components (spec-tool-policy-control.tsx,
// spec-tool-row.tsx) can reuse this single ordering and label set instead of
// declaring their own -- two copies of "always_allow < needs_approval <
// blocked" that could quietly drift apart is exactly the defect this avoids.
export const DECISION_STRICTNESS: Readonly<Record<SpecToolDecision, number>> = {
  always_allow: 0,
  needs_approval: 1,
  blocked: 2,
};

export const DECISION_LABELS: Readonly<Record<SpecToolDecision, string>> = {
  always_allow: 'Always allow',
  needs_approval: 'Needs approval',
  blocked: 'Blocked',
};

/**
 * Splits one category's rows into what a group-default apply would actually
 * do, without a round trip: category, registryFloor, workspaceDecision, and
 * revision are all already in the list response the page fetched, so a
 * preview endpoint would add latency and a new auth surface for zero new
 * information.
 *
 * A row belongs to `exceptions` when `decision` would loosen it below its own
 * registry floor (an apply must never send that write), `unchanged` when
 * applying would not move the effective policy, and `applicable` otherwise.
 *
 * "Would not move the effective policy" is two cases, not one. The obvious one
 * is a stored `workspaceDecision` already equal to `decision`. The second is a
 * row with NO stored workspace decision whose registry floor already equals
 * `decision`: writing it stamps a new revision and a fresh "Changed by <admin>
 * on <date>" onto a tool nobody meaningfully changed, on a page whose whole job
 * is showing provenance. That second case is only sound because
 * `defaultPolicy === registryFloor` for every native tool, which
 * packages/tools/src/native-registry.test.ts asserts as an equality precisely
 * so this comparison stays honest -- read the comment there before letting the
 * two diverge.
 */
export function previewWorkspaceToolGroupPolicy(
  rows: readonly SpecToolSettingsRow[],
  decision: SpecToolDecision,
): SpecToolGroupPolicyPreview {
  const applicable: SpecToolSettingsRow[] = [];
  const exceptions: SpecToolGroupPolicyException[] = [];
  const unchanged: SpecToolSettingsRow[] = [];

  // No membership filter here on purpose. This used to also take a `category`
  // and skip rows outside it, which duplicated a decision the caller had
  // already made: the page passes exactly the rows of the group it is applying
  // to, already narrowed by the active search. The duplicate was worse than
  // redundant -- it silently hard-coded ONE grouping, so regrouping the page by
  // risk class emptied every preview and the confirmation dialog would have
  // reported "0 will change" for a bulk apply that was in fact ready to run.
  for (const row of rows) {
    if (DECISION_STRICTNESS[decision] < DECISION_STRICTNESS[row.registryFloor]) {
      exceptions.push({
        // Leads with the floor so no possessive is needed: a title derived from
        // a tool name can end in "s" (workspace_manage_views), and this string
        // is rendered verbatim in the confirmation dialog.
        toolName: row.name,
        reason:
          `The registry floor for ${row.title} is "${DECISION_LABELS[row.registryFloor]}", so ` +
          `it cannot be loosened to "${DECISION_LABELS[decision]}".`,
      });
      continue;
    }

    if (row.workspaceDecision === decision) {
      unchanged.push(row);
      continue;
    }

    if (row.workspaceDecision === null && row.registryFloor === decision) {
      unchanged.push(row);
      continue;
    }

    applicable.push(row);
  }

  return { applicable, exceptions, unchanged };
}

export interface SpecToolGroupPolicyFailure {
  toolName: string;
  code: string;
  message: string;
}

export interface SpecToolGroupPolicyApplyResult {
  updated: readonly SpecToolSettingsRow[];
  failures: readonly SpecToolGroupPolicyFailure[];
}

/**
 * Owner/admin only. Writes one `decision` across every tool named in
 * `expectedRevisions` (normally the `applicable` rows a preview just
 * computed), each guarded by its own revision precondition. The batch route
 * is never all-or-nothing, so this never throws for a per-item rejection --
 * it splits `results` into `updated` and `failures` instead. A caller that
 * wants failures thrown must do that itself.
 */
export async function applyWorkspaceToolGroupPolicy(
  workspaceId: string,
  input: { decision: SpecToolDecision; expectedRevisions: Readonly<Record<string, number>> },
): Promise<SpecToolGroupPolicyApplyResult> {
  const inputs = Object.entries(input.expectedRevisions).map(([toolName, expectedRevision]) => ({
    toolName,
    decision: input.decision,
    expectedRevision,
  }));

  const wire = await apiFetch<SpecToolBatchWireResponse>(
    `/workspaces/${workspaceId}/spec/tools/batch`,
    { method: 'POST', body: JSON.stringify({ inputs }) },
  );

  const updated: SpecToolSettingsRow[] = [];
  const failures: SpecToolGroupPolicyFailure[] = [];
  for (const result of wire.results) {
    // The batch response names each result by array INDEX, not tool name
    // (see batchItemResultSchema in apps/api/src/routes/spec-tools.ts) --
    // map back through the request just built, which is in the same order.
    const toolName = inputs[result.index]?.toolName ?? `#${result.index}`;
    if (result.status === 'ok') {
      updated.push(toSpecToolSettingsRow(result.record));
    } else {
      failures.push({ toolName, code: result.error.code, message: result.error.message });
    }
  }
  return { updated, failures };
}
